import type { CSSProperties, FC } from "react"
import * as NucleoFillIcons from "nucleo-ui-fill-18"

interface Props {
	icon?: string | null
	className?: string
	style?: CSSProperties
}

// Translate well-known lucide-react icon names to their nucleo-ui-fill-18
// equivalents so legacy DB values (chosen with the lucide picker) keep
// rendering a sensible icon after the migration. Keys are lucide PascalCase
// roots; values are nucleo roots (without the `Icon`/`Fill18` wrap).
// Semantic mismatches — lucide names that have no obvious algorithmic mapping
// to nucleo. Heuristics below cover the algorithmic cases (digit suffixes,
// Off→Slash, X→Xmark, Circle/Square wrap swaps, etc.).
const LUCIDE_TO_NUCLEO: Record<string, string> = {
	// settings / config
	Settings: "Gear",
	Settings2: "Gear",
	Cog: "Gear",
	Sliders: "Sliders",
	SlidersHorizontal: "Sliders",
	SlidersVertical: "Sliders",
	// mail
	Mail: "Envelope",
	Mails: "Envelopes",
	MailOpen: "EnvelopeOpen",
	MailCheck: "EnvelopeCheck",
	MailPlus: "EnvelopePlus",
	MailMinus: "EnvelopeMinus",
	MailX: "EnvelopeBan",
	MailWarning: "EnvelopeAlert",
	MailQuestion: "Envelope",
	Mailbox: "Mailbox",
	// power
	Zap: "Bolt",
	ZapOff: "BoltSlash",
	// AI / sparkle (matches the project rule: use Atom for sparkle/bot)
	Sparkles: "Atom",
	Sparkle: "Atom",
	Bot: "Atom",
	Robot: "Atom",
	BotMessageSquare: "ChatBot",
	// search
	Search: "Magnifier",
	SearchCheck: "MagnifierCheck",
	SearchX: "MagnifierSlash",
	SearchSlash: "MagnifierSlash",
	SearchCode: "Magnifier",
	ZoomIn: "MagnifierPlus",
	ZoomOut: "MagnifierMinus",
	// edit
	Edit: "Pencil",
	Edit2: "Pencil",
	Edit3: "Pencil",
	Pen: "Pencil",
	PenLine: "Pencil",
	PenSquare: "EditSquare",
	PenTool: "Pencil",
	PencilLine: "Pencil",
	PencilRuler: "Pencil",
	Eraser: "Eraser",
	// trash / delete
	Trash2: "Trash",
	Delete: "Trash",
	// chat
	MessageCircle: "ChatBubble",
	MessageSquare: "ChatBubble",
	MessagesSquare: "Chats",
	MessageCircleQuestion: "ChatBubbleQuote",
	MessageCircleHeart: "ChatBubbleHeart",
	MessageCirclePlus: "ChatBubblePlus",
	MessageCircleMore: "ChatBubble",
	MessageCircleX: "ChatBubble",
	MessageSquareText: "ChatBubbleContent",
	MessageSquareHeart: "ChatBubbleHeart",
	MessageSquarePlus: "ChatBubblePlus",
	MessageSquareQuote: "ChatBubbleQuote",
	MessageSquareLock: "ChatBubbleLock",
	MessageSquareCheck: "ChatBubbleCheck",
	MessageSquareReply: "ChatBubbleForward",
	Speech: "ChatBubble",
	// help / info / alert
	HelpCircle: "CircleQuestion",
	CircleHelp: "CircleQuestion",
	AlertCircle: "AlertWarning",
	AlertTriangle: "TriangleWarning",
	TriangleAlert: "TriangleWarning",
	AlertOctagon: "AlertWarning",
	OctagonAlert: "AlertWarning",
	Info: "CircleInfo",
	BadgeAlert: "AlertWarning",
	BadgeHelp: "CircleQuestion",
	BadgeInfo: "CircleInfo",
	// check / x
	CheckCircle: "CircleCheck",
	CheckCircle2: "CircleCheck",
	CheckCheck: "CheckDouble",
	BadgeCheck: "CircleCheck",
	XCircle: "CircleXmark",
	X: "Xmark",
	BadgeX: "CircleXmark",
	// arrows / more
	MoreHorizontal: "Dots",
	MoreVertical: "DotsVertical",
	Ellipsis: "Dots",
	EllipsisVertical: "DotsVertical",
	// formatting
	Hash: "Hashtag",
	// currency
	DollarSign: "CurrencyDollar",
	Euro: "CurrencyEuro",
	EuroSign: "CurrencyEuro",
	PoundSterling: "CurrencySterling",
	IndianRupee: "CurrencyRupee",
	JapaneseYen: "CurrencyYen",
	Banknote: "Money",
	Coins: "Coin",
	PiggyBank: "PiggyBank",
	// faces
	Smile: "FaceSmile",
	SmilePlus: "FaceSmile",
	Frown: "FaceDisappointed",
	Meh: "FaceDisappointed",
	Laugh: "FaceGrin",
	Angry: "FaceAngry",
	// file
	FileText: "FilePen",
	FileEdit: "FilePen",
	Files: "Copy",
	// notes / books
	StickyNote: "Note",
	NotebookText: "Notebook",
	NotebookPen: "Notebook",
	BookText: "Book",
	BookMarked: "BookmarkedBook",
	BookOpenText: "BookOpen",
	BookOpenCheck: "BookOpen",
	// refresh / loader
	RotateCw: "Refresh",
	RotateCcw: "Refresh",
	RefreshCw: "Refresh",
	RefreshCcw: "Refresh",
	Loader2: "Loader",
	LoaderCircle: "Loader",
	// send / share
	Send: "PaperPlane",
	SendHorizontal: "PaperPlane",
	SendToBack: "PaperPlane",
	// link
	Link2: "Link",
	Link2Off: "LinkBroken",
	Unlink: "LinkBroken",
	Unlink2: "LinkBroken",
	// volume / mic / wifi
	VolumeX: "VolumeOff",
	Volume1: "Volume",
	Volume2: "Volume",
	MicOff: "MicrophoneSlash",
	Mic: "Microphone",
	MicVocal: "Microphone",
	// building
	Building2: "Office2",
	Building: "Office",
	Factory: "Office",
	Warehouse: "Office",
	Home: "House",
	HomeIcon: "House",
	// shopping
	Receipt: "Receipt",
	ShoppingCart: "BasketShopping",
	ShoppingBag: "Bag",
	ShoppingBasket: "BasketShopping",
	// users
	UserX: "UserBan",
	UserSearch: "User",
	Users2: "Users",
	UserSquare: "User",
	UserRound: "User",
	UserRoundPlus: "UserPlus",
	UserRoundMinus: "UserMinus",
	UserRoundCheck: "UserCheck",
	UserRoundX: "UserBan",
	UserRoundPen: "UserPen",
	UserRoundCog: "UserSettings",
	UserCog: "UserSettings",
	UserRoundSearch: "User",
	CircleUserRound: "CircleUser",
	UserCircle: "CircleUser",
	SquareUserRound: "SquareUser",
	PersonStanding: "User",
	Contact: "AddressBook",
	Contact2: "AddressBook",
	// charts
	BarChart: "ChartBar",
	BarChart2: "ChartBar",
	BarChart3: "ChartBar",
	BarChart4: "ChartBar",
	BarChartHorizontal: "ChartBar",
	LineChart: "ChartLine",
	PieChart: "ChartPie",
	TrendingUp: "ArrowTrendUp",
	TrendingDown: "ArrowTrendDown",
	// navigation
	Navigation2: "Navigation",
	// shapes
	Crosshair: "Crosshairs",
	// misc
	ExternalLink: "ArrowFromCornerUpRight",
	LogIn: "EnterDoor",
	LogOut: "ExitDoor",
	Globe2: "Globe",
	Earth: "Globe",
	PlaneTakeoff: "PlaneTakeOff",
	Wand: "MagicWand",
	Wand2: "MagicWand",
	// extras for common variants without algorithmic matches
	BellRing: "Bell",
	HeartCrack: "Heart",
	ClockAlert: "Clock",
	CalendarRange: "Calendar",
	FolderClosed: "Folder",
	FolderEdit: "FolderPen",
	FolderInput: "FolderImport",
	FolderOutput: "FolderImport",
	FolderDown: "FolderDownload",
	FolderUp: "FolderImport",
	FolderRoot: "Folder",
	FolderArchive: "Folder",
	FolderGit: "Folder",
	FolderGit2: "Folder",
	Boxes: "Box",
	Package: "Box",
	Package2: "Box",
	PackageOpen: "Box",
	PackageCheck: "BoxCheck",
	PackagePlus: "BoxPlus",
	PackageMinus: "BoxMinus",
	PackageX: "Box",
	PackageSearch: "Box",
	LockKeyhole: "Lock",
	Unlock: "LockOpen",
	KeySquare: "Key",
	CodeXml: "Code",
	Cpu: "Microchip",
	Component: "Cube",
	LayoutDashboard: "Grid2",
	LayoutGrid: "Grid2",
	LayoutList: "BulletList",
	List: "BulletList",
	ListChecks: "CheckList",
	ListTodo: "CheckList",
	ListOrdered: "BulletList",
	ListPlus: "BulletList",
	ListMinus: "BulletList",
	BookmarkX: "BookmarkSlash",
	Bookmarked: "Bookmark",
	FilterX: "Filter2Off",
	SignalHigh: "Signal",
	SignalLow: "Signal",
	SignalMedium: "Signal",
	SignalZero: "SignalOff",
	SunDim: "Sun",
	SunMedium: "Sun",
	MoonStar: "Moon",
	Cloudy: "Cloud",
	CloudRain: "CloudDroplet",
	CloudSnow: "Cloud",
	Droplets: "Droplet",
}

const swapWrapper = (name: string): string | null => {
	// PlusCircle → CirclePlus, UserCircle → CircleUser, etc.
	const suffix = ["Circle", "Square"]
	for (const w of suffix) {
		if (name.endsWith(w) && name.length > w.length) {
			return `${w}${name.slice(0, -w.length)}`
		}
		if (name.startsWith(w) && name.length > w.length) {
			return `${name.slice(w.length)}${w}`
		}
	}
	return null
}

const resolveExport = (icon: string | null | undefined): string | undefined => {
	if (!icon) return undefined

	const tryName = (candidate: string): string | undefined => {
		const exportName = `Icon${candidate}Fill18`
		return exportName in NucleoFillIcons ? exportName : undefined
	}

	// 1. direct
	const direct = tryName(icon)
	if (direct) return direct

	// 2. explicit map
	const mapped = LUCIDE_TO_NUCLEO[icon]
	if (mapped) {
		const mappedExport = tryName(mapped)
		if (mappedExport) return mappedExport
	}

	// 3. algorithmic transforms — applied in order, returning the first hit
	const transforms: Array<(s: string) => string | null> = [
		// strip trailing digit: Trash2 → Trash, Music3 → Music
		(s) => {
			const stripped = s.replace(/\d+$/, "")
			return stripped !== s ? stripped : null
		},
		// trailing Off → Slash: BellOff → BellSlash
		(s) => (s.endsWith("Off") ? `${s.slice(0, -3)}Slash` : null),
		// trailing Off → bare: BellOff → Bell (fallback)
		(s) => (s.endsWith("Off") ? s.slice(0, -3) : null),
		// trailing X → Xmark: FileX → FileXmark
		(s) =>
			s.endsWith("X") && !s.endsWith("Xmark") ? `${s.slice(0, -1)}Xmark` : null,
		// leading X → Xmark
		(s) => (s === "X" ? "Xmark" : null),
		// swap Circle/Square wrapper position
		swapWrapper,
		// strip trailing Round: UserRound → User
		(s) => (s.endsWith("Round") ? s.slice(0, -5) : null),
		// strip trailing Big: CircleCheckBig → CircleCheck
		(s) => (s.endsWith("Big") ? s.slice(0, -3) : null),
	]

	for (const transform of transforms) {
		const candidate = transform(icon)
		if (!candidate) continue
		const hit = tryName(candidate)
		if (hit) return hit
		// also try mapping the transformed candidate
		const mappedCandidate = LUCIDE_TO_NUCLEO[candidate]
		if (mappedCandidate) {
			const mh = tryName(mappedCandidate)
			if (mh) return mh
		}
	}

	return undefined
}

export type NucleoIconComponent = React.FC<React.SVGProps<SVGSVGElement>>

export const DEFAULT_ICON_NAME = "BookOpen"

export function resolveNucleoFillIcon(
	icon: string | null | undefined,
): NucleoIconComponent {
	const exportName = resolveExport(icon)
	const Icon = exportName
		? (NucleoFillIcons[exportName as keyof typeof NucleoFillIcons] as
				| NucleoIconComponent
				| undefined)
		: undefined
	return Icon ?? NucleoFillIcons.IconBookOpenFill18
}

export const NucleoFillIcon: FC<Props> = ({ icon, className, style }) => {
	const Icon = resolveNucleoFillIcon(icon)
	return <Icon className={className} style={style} />
}
